// ─── Ajout / édition d'une période de rythme de garde — modale ───
// Ouverte depuis FeuilleGardes (➕ ou swipe Éditer). Rendue en SŒUR (calque)
// pour ne pas imbriquer deux ScrollViews. Contient : choix du modèle (intégrés
// + personnalisés + création), jour de départ (SelecteurDate), aperçu de la
// fin calculée / recouvrement.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, SelecteurDate, HIT } from '@mop/core';
import { MODELES_GARDE } from './services/modelesGarde';
import { apercuInsertion, frCourt } from './services/rythmesGarde';
import FeuilleModelePerso from './FeuilleModelePerso';

export default function FeuilleRythme({ visible, mode, initModeleId, initDebut, initFin, estDernier = true, perso = [], rythmes = [], parents, onValider, onEnregistrerPerso, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [modeleId, setModeleId] = useState('alternee');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');           // date de fin (édition) ; '' = sans fin
  const [err, setErr] = useState('');
  const [pickDate, setPickDate] = useState(false);
  const [pickFin, setPickFin] = useState(false);
  const [persoVisible, setPersoVisible] = useState(false);
  const [persoEnCours, setPersoEnCours] = useState(null);

  useEffect(() => {
    if (!visible) return;
    setModeleId(initModeleId || 'alternee');
    setDebut(initDebut || '');
    setFin(initFin || '');
    setErr('');
  }, [visible, initModeleId, initDebut, initFin]);

  const options = [
    ...MODELES_GARDE.map(m => ({ id: m.id, label: t(m.i18n), desc: t(m.i18nDesc), perso: false })),
    ...perso.map(m => ({ id: m.id, label: m.nom, desc: t('garde.cyclePersoDesc', { n: m.nbSemaines || 2 }) + (m.cale ? (m.semaine1 === 'impaire' ? t('garde.caleS1Impaires') : t('garde.caleS1Paires')) : ''), perso: true, modele: m })),
  ];
  const apercu = mode === 'add' ? apercuInsertion(rythmes, debut) : { fin: null, err: null };

  function valider() {
    const r = onValider?.(mode === 'add' ? { modeleId, debut } : { modeleId, debut, fin: fin || null });
    if (r && r.ok === false) { setErr(r.err ? t(r.err, r.errP) : t('messagerie.enregistrerImpossible')); return; }
    onFermer?.();
  }
  function enregistrerPerso(m) {
    onEnregistrerPerso?.(m);
    setModeleId(m.id);
    setPersoVisible(false);
  }

  // Disquette grisée tant que rien n'a changé (principe app-wide) / si invalide.
  const modifie = mode === 'add'
    ? true
    : (modeleId !== (initModeleId || 'alternee') || debut !== (initDebut || '') || (fin || '') !== (initFin || ''));
  const bloque = mode === 'add' && !!apercu.err;
  const action = { icone: mode === 'add' ? '＋' : '💾', onPress: valider, disabled: !debut || bloque || !modifie, label: mode === 'add' ? t('commun.ajouter') : t('commun.enregistrer') };

  return (
   <>
    <Feuille visible={visible} titre={mode === 'add' ? t('garde.nouvellePeriode') : t('garde.modifierPeriode')} emoji="🗓️" hauteur={0.82} onFermer={onFermer} action={action}>
      <View style={s.lblRow}>
        <Text style={[s.lbl, { marginBottom: 0 }]}>{t('garde.modeleGarde')}</Text>
        <TouchableOpacity onPress={() => { setPersoEnCours(null); setPersoVisible(true); }} hitSlop={HIT} accessibilityLabel={t('garde.nouveauModelePersoAcc')}>
          <Text style={s.lblAjout}>＋</Text>
        </TouchableOpacity>
      </View>
      {options.map(o => (
        <TouchableOpacity key={o.id} style={[s.opt, modeleId === o.id && s.optOn]} onPress={() => { setModeleId(o.id); setErr(''); }}>
          <Text style={s.radio}>{modeleId === o.id ? '◉' : '○'}</Text>
          <View style={{ flex: 1 }}>
            <Text style={s.optNom}>{o.label}</Text>
            <Text style={s.optDesc}>{o.desc}</Text>
          </View>
          {o.perso && (
            <TouchableOpacity onPress={() => { setPersoEnCours(o.modele); setPersoVisible(true); }} hitSlop={HIT} accessibilityLabel={t('garde.modifierModele')}>
              <Text style={s.optEdit}>✏️</Text>
            </TouchableOpacity>
          )}
        </TouchableOpacity>
      ))}
      <Text style={s.lbl}>{t('garde.aPartirDuJourDepart')}</Text>
      <TouchableOpacity style={s.champBtn} onPress={() => setPickDate(true)}>
        <Text style={[s.champVal, !debut && s.champVide]}>📅 {debut ? frCourt(debut) : t('ft.choisir')}</Text>
      </TouchableOpacity>

      {mode === 'edit' && (
        <>
          <Text style={s.lbl}>{t('finances.dateFin')}</Text>
          <TouchableOpacity style={s.champBtn} onPress={() => setPickFin(true)}>
            <Text style={[s.champVal, !fin && s.champVide]}>📅 {fin ? frCourt(fin) : (estDernier ? t('finances.rrSansFin') : t('ft.choisir'))}</Text>
          </TouchableOpacity>
          {estDernier && <Text style={s.aide}>{t('garde.laisseSansFinAide')}</Text>}
        </>
      )}

      {mode === 'add' && debut ? (
        apercu.err
          ? <Text style={s.err}>⚠️ {t(apercu.err)}</Text>
          : <Text style={s.apercu}>{t('garde.finCalculee', { fin: apercu.fin ? frCourt(apercu.fin) : t('garde.sansFinMin') })}</Text>
      ) : null}
      {!!err && <Text style={s.err}>{err}</Text>}
    </Feuille>

    {/* Sœurs empilées (calques) : sélecteur de date + éditeur de modèle perso. */}
    <FeuilleModelePerso visible={persoVisible} modele={persoEnCours} parents={parents}
      onEnregistrer={enregistrerPerso} onFermer={() => setPersoVisible(false)} />
    <SelecteurDate visible={pickDate} titre={t('garde.jourDepart')} valeur={debut}
      onChange={(dk) => { setDebut(dk); setErr(''); }} onFermer={() => setPickDate(false)} />
    <SelecteurDate visible={pickFin} titre={t('finances.dateFin')} valeur={fin} min={debut} avecEffacer={estDernier}
      onChange={(dk) => { setFin(dk); setErr(''); }} onFermer={() => setPickFin(false)} />
   </>
  );
}


const makeStyles = (UI) => StyleSheet.create({
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  lblRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, marginBottom: 7 },
  lblAjout: { fontSize: 20, color: UI.accent, fontWeight: '600' },
  opt: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 8, borderRadius: 10, marginBottom: 4 },
  optOn: { backgroundColor: UI.accentTint },
  radio: { fontSize: 17, color: UI.accent, width: 20, textAlign: 'center' },
  optNom: { fontSize: 14, fontWeight: '700', color: UI.ink },
  optDesc: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  optEdit: { fontSize: 15, paddingHorizontal: 4 },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 12, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  champVide: { color: UI.faint, fontWeight: '400' },
  apercu: { fontSize: 12, color: UI.muted, fontWeight: '600', marginTop: 9 },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 7, lineHeight: 16 },
  err: { fontSize: 12, color: '#C62828', marginTop: 9 },
});
